// Crew — Office records. Personnel, who is on the clock, one worker's profile.
//
//   GET /api/workers[?include_inactive=true]   list; hourly_rate is STRIPPED by the
//                                              server without workers.read_sensitive,
//                                              so the column is absent here too —
//                                              not blank, absent.
//   GET /api/workers/status/clocked-in         who is on the clock AND who is
//                                              not — both arrays it answers
//                                              with, since 2026-09-09
//   GET /api/workers/:id/jobs                  a worker's recent jobs
//   PUT /api/workers/:id                       profile edits
//   GET /api/time-entries/paystub/:id          PDF, fetched as a blob
//
// Those middle two are checkRole('admin', 'manager') — a ROLE list, not a
// permission. WORKER_ENDPOINTS below is that contract written down once, and
// OfficeCrew.test.js checks it against the route file on every run.
//
// SSNs and the encryption passkey are NOT handled here, on purpose (CLAUDE.md
// rule 5). The profile links to the Security screen for that.

import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { put } from '../../shared/http';
// The ONE role vocabulary (apps/shared/roles.js). This screen printed `w.role`
// raw — "worker" — while /users two taps away said "Crew", for the same person.
import { roleLabel } from '../../shared/roles';
import NewAccountSheet, { WelcomeNotice } from '../admin/NewAccountSheet';
import { useCan, useRole } from '../../../hooks/usePermission';
import { useSettings } from '../../../App';
import { DataTable, Pill, Avatar, FwButton, FwField, FwInput, FwTextarea, Skeleton, EmptyState, SectionLabel, BigClock, initialsOf, formatClock, elapsedSeconds, usePhoneLayout } from '../../../ui/fw';
// `timeAgo` is this section's ONE reading of "how long since" — written with a
// parseUTCDate guard because a bare SQLite timestamp read as local time puts
// every row hours in the future (insight/reportsContract.js).
import { timeAgo } from '../insight/reportsContract';
import { parseUTCDate } from '../../../utils/timezone';
import { getCurrentWeekRange } from '../../../utils/weekUtils';
import {
  useOfficeQuery, useAction, downloadBlob, PageHeader, Toolbar, SearchBox, Panel, Split, DetailList, DetailRow,
  ActivePill, LoadError, FormError, FormRow, matches, textOr, factFallback, fmtDate, fmtDateTime, fmtRate, hoursOf, jobTone, ROLE_TONE,
} from './OfficePage';

/**
 * The two workers.js routes whose gate this screen mirrors, written down once.
 * CLAUDE.md: "Declare a contract once, and check it against the server."
 * OfficeCrew.test.js re-reads server/routes/workers.js and fails if any of
 * this drifts — in either direction.
 *
 * Both are `checkRole('admin', 'manager')`, which server/middleware/auth.js
 * tests with `allowedRoles.includes(req.user.role)` and NOTHING else. A
 * per-user extra_permissions grant cannot open either door. So the question
 * this screen has to ask is the ROLE, and asking `useCan` instead was a second
 * opinion that disagreed with the server in BOTH directions at once:
 *
 *   a MANAGER holds `workers: ['read']` in server/database.js — no `update` —
 *   so `useCan('workers', 'update')` hid Edit profile from the one role
 *   besides admin that PUT /api/workers/:id actually admits. Correcting a crew
 *   member's address or phone number is a daily job (workers.js says exactly
 *   that above the route), and the rebuild took it away without saying so.
 *
 *   an ACCOUNTING user holds `workers: ['read', 'read_sensitive']`, so
 *   navigation.js puts them on this screen and they may open any record — and
 *   GET /workers/:id/jobs, which used to be fetched for whoever opened one,
 *   answers them 403 every single time.
 *
 * Which side of each disagreement is right is a permission decision for a
 * human (CLAUDE.md rule 5) and both routes carry a comment saying so; they are
 * two of the twenty-one in server/utils/__tests__/roleGateDivergence.test.js.
 * Until somebody settles them, the screen mirrors the DOOR, not the model.
 *
 * `workers.read_sensitive` is deliberately not in here. The server tests that
 * one with userHasPermission (scrubSensitiveFields in routes/workers.js),
 * which reads the same role-baseline-∪-extras union `useCan` reads, so it
 * cannot diverge by construction — and `canSensitive` below still asks it.
 */
export const WORKER_ENDPOINTS = {
  updateProfile: { method: 'PUT', path: '/workers/:id', roles: ['admin', 'manager'] },
  recentJobs: { method: 'GET', path: '/workers/:id/jobs', roles: ['admin', 'manager'] },
};

/**
 * May this role use that endpoint?
 *
 * Unknown name, or no role yet because the session is still hydrating → false.
 * Never true by accident: a control that appears and then vanishes is worse
 * than one that arrives a beat late.
 */
export function mayUseWorkerEndpoint(name, role) {
  const e = WORKER_ENDPOINTS[name];
  if (!e || !role || !Array.isArray(e.roles)) return false;
  return e.roles.includes(role);
}

const PROFILE_FIELDS = ['name', 'email', 'phone_number', 'address', 'city', 'state', 'zip_code', 'emergency_contact', 'emergency_phone', 'hire_date', 'notes'];

function seed(w, canSensitive) {
  const f = {};
  PROFILE_FIELDS.forEach((k) => { f[k] = w[k] === null || w[k] === undefined ? '' : String(w[k]); });
  f.hire_date = f.hire_date.slice(0, 10);
  if (canSensitive) f.hourly_rate = w.hourly_rate === null || w.hourly_rate === undefined ? '' : String(w.hourly_rate);
  return f;
}

/** The PUT body: only what changed, hourly_rate only when it may be written. */
export function profileBody(form, original, canSensitive) {
  const body = {};
  PROFILE_FIELDS.forEach((k) => {
    const before = original[k] === null || original[k] === undefined ? '' : String(original[k]);
    if (k === 'hire_date' ? form.hire_date !== before.slice(0, 10) : form[k] !== before) {
      body[k] = k === 'hire_date' && form.hire_date === '' ? null : form[k];
    }
  });
  if (canSensitive) {
    const before = original.hourly_rate === null || original.hourly_rate === undefined ? '' : String(original.hourly_rate);
    if (form.hourly_rate !== before && form.hourly_rate !== '') body.hourly_rate = parseFloat(form.hourly_rate);
  }
  return body;
}

// One worker's recent jobs. There is no "who" to put under the title — the
// worker IS the panel — so the card is headline, status badge and the three
// facts that say how the job went: hours, tasks done, when it last moved.
const JOB_COLUMNS = [
  { key: 'title', label: 'Job', role: 'title' },
  { key: 'status', label: 'Status', role: 'badge', render: (j) => <Pill tone={jobTone(j.status)}>{String(j.status || 'unknown').replace(/_/g, ' ')}</Pill> },
  { key: 'total_time', label: 'Hours', align: 'right', role: 'fact', render: (j) => hoursOf(j.total_time) },
  { key: 'subtasks', label: 'Tasks', align: 'right', role: 'fact', render: (j) => `${j.subtasks_completed ?? 0}/${j.subtask_count ?? 0}` },
  { key: 'updated_at', label: 'Updated', role: 'fact', render: (j) => fmtDateTime(j.updated_at) },
];

/**
 * When somebody who is NOT on the clock last was — "3d ago", or the date once
 * that stops being useful, or the absence said out loud.
 *
 * `last_clock_out` is `MAX(end_time)` over that person's entries
 * (server/routes/workers.js), so NULL means they have never finished a shift:
 * a new hire, or somebody who has only ever been clocked in. "Never" and "we
 * do not know" are different claims and only the first one is true, so it says
 * the first.
 *
 * Through `timeAgo`, which parses a bare SQLite timestamp as UTC. `new Date()`
 * on "2026-09-08 23:30:00" reads it as LOCAL, which in Eastern puts every row
 * four hours in the future and collapses the first four hours into "just now"
 * — the classic Dashboard carried that bug and the rebuild must not reacquire
 * it.
 */
export function lastWorked(worker) {
  const stamp = worker && worker.last_clock_out;
  if (!stamp) return 'No hours recorded yet';
  const ago = timeAgo(stamp);
  return `Last worked ${ago || fmtDate(stamp)}`;
}

/**
 * Who is on the clock, and who is not.
 *
 * ── The half that was returned and never drawn (2026-09-09) ────────────────
 *
 * GET /workers/status/clocked-in answers `{ clocked_in, clocked_out }` and
 * this screen rendered only the first array. The classic Dashboard's "Worker
 * Status" card showed both — two counts side by side, "Clocked in N" and "Off
 * clock N" (`git show d25258b^:client/src/components/Dashboard/Dashboard.js`
 * L709–L733) — and docs/PARITY_2026-09-08.md §18 records that the rebuild
 * dropped it.
 *
 * It shows more than the classic did, because the server carries more than the
 * classic drew: `clocked_out[].last_clock_out` is on every row, and "when did
 * this person last work" was, in the parity doc's words, a figure that existed
 * nowhere in the product. A dispatcher asking "who can I send" needs the name
 * AND that date — a count of eleven says nothing about which eleven.
 *
 * Both lists are rendered only when the request actually ANSWERED. `q.data`,
 * not `list.length`: a 403 or a 500 leaves both arrays empty, and "Nobody is
 * on the clock right now" over a failed request is a false statement about the
 * crew rather than a visible failure. Same reasoning as pickerNote.js.
 */
/**
 * How long this person has been on the clock, in seconds — or null when the
 * answer carries no start to measure from.
 *
 * ── Why not `minutes_elapsed` (2026-09-10) ────────────────────────────────
 *
 * The owner: "Timer running should show seconds, so we can see timer is
 * actually running." This strip drew `formatClock(minutes_elapsed * 60)`, so
 * the figure was a MINUTE count printed as H:MM off a query with no tick at
 * all. It could not move for a whole minute even in principle — a photograph
 * of a clock, on the panel titled "Clocked in now".
 *
 * The start was already on the wire: server/routes/workers.js selects
 * `te.start_time` in the same statement as the minute count. It is a BARE
 * SQLite stamp — "2026-09-09 19:26:53", UTC with nothing marking it — and
 * `new Date()` reads that as LOCAL, which is four or five hours out in
 * Connecticut and is exactly the fault apps/shared/liveClock.js refuses
 * /api/time-entries/active/all over. Hence `parseUTCDate`, which is this
 * client's one reader of both stored shapes.
 *
 * A start instant rather than an `elapsedNow` anchor because this response
 * carries no server elapsed in seconds to anchor to — only whole minutes. So
 * this is the same arithmetic Field Today's own clock does (`elapsedSeconds`,
 * one implementation, in ui/fw/format.js), with the same exposure to a device
 * clock that is wrong and the same clamp at 0:00 inside BigClock.
 *
 * Null, not 0, when there is no start: `new Date(null).getTime()` is 0, which
 * would render a fifty-six-year shift, and a fabricated figure on a screen
 * about people's hours is worse than an absent one.
 */
export function clockedInSeconds(worker, now = Date.now()) {
  const started = parseUTCDate(worker && worker.start_time);
  const ms = started ? started.getTime() : NaN;
  if (!Number.isFinite(ms)) return null;
  return elapsedSeconds(ms, now);
}

function ClockedInStrip() {
  const q = useOfficeQuery(['workers', 'clocked-in'], '/workers/status/clocked-in');
  const list = (q.data && q.data.clocked_in) || [];
  // ONE interval for the whole strip, not one per chip: eight people on the
  // clock is eight rows off a single answer, and eight timers would be eight
  // renders a second to redraw one list. Off entirely when nobody is on the
  // clock, so an idle office tab does no work.
  const [now, setNow] = useState(() => Date.now());
  const live = list.length;
  useEffect(() => {
    if (!live) return undefined;
    setNow(Date.now());
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [live]);
  // `Array.isArray`, not `|| []`. An old build that does not answer this half
  // of the question would otherwise render "Everybody is on the clock." — a
  // statement about the crew made from no data, which this repo has repeatedly
  // recorded as worse than a visible error. No array, no section.
  const off = q.data && Array.isArray(q.data.clocked_out) ? q.data.clocked_out : null;
  return (
    <Panel title={`Clocked in now${q.data ? ` (${q.data.total_clocked_in ?? list.length})` : ''}`}>
      {q.isPending ? <Skeleton lines={1} /> : null}
      <LoadError error={q.error} fallback="Could not load who is clocked in." />
      {/*
        What this panel counts, said once, because it is NOT the list below it.
        `clocked_in` has no role filter at all — an admin on the clock is in it
        and is never on the roster — and `clocked_out` is every active
        worker/manager/accounting whoever is asking, while GET /workers narrows
        to what this reader may open. So the two numbers on this screen are
        different populations, and until this sentence existed a manager read
        "1 person" above "Not on the clock (3)" with nothing saying why.

        Kept to ONE line at 390px, deliberately. The first draft read
        "Company-wide. This can include people who are not on the list below."
        and wrapped to two, which measured the first crew card from y=736 to
        y=790 — off a 780px screen entirely, on a list the 2026-09-10 audit had
        already flagged as starting too low. A sentence explaining a layout
        must not cost more than the confusion it removes.

        Only rendered once the request has actually answered, for the same
        reason the two lists are — a scope sentence over a failed request
        describes nothing.
      */}
      {q.data ? (
        <p className="or-strip__scope">Company-wide, not only the list below.</p>
      ) : null}
      {q.data ? (
          list.length === 0 ? <p className="or-strip__empty">Nobody is on the clock right now.</p> : (
            <div className="or-strip">
              {list.map((w) => {
                const seconds = clockedInSeconds(w, now);
                return (
                <div key={w.entry_id || w.id} className="or-strip__chip" data-tone="now">
                  <Avatar initials={initialsOf(w.name)} tone="now" />
                  <div className="or-strip__who">
                    <div className="or-strip__name">{w.name}</div>
                    <div className="or-strip__meta">{w.job_title || 'No job'}</div>
                  </div>
                  {/* A row the server gave no start for keeps the old H:MM
                      off its minute count. Seconds that cannot move are a
                      stronger lie than no seconds at all. */}
                  {seconds === null
                    ? <span className="or-strip__clock fw-num">{formatClock((Number(w.minutes_elapsed) || 0) * 60)}</span>
                    : <BigClock className="or-strip__clock" size="sm" seconds={seconds} />}
                </div>
                );
              })}
            </div>
          )
        ) : null}

      {/* Inside the same Panel and off the same request: two sections, one
          question. A second query against this endpoint would double the
          screen's cost for data it already has, and server.js rate-limits
          /api/ to 1000 requests per 15 minutes PER IP with a whole crew
          behind one address. */}
      {off ? (
        <div className="or-offclock" data-section="off-clock">
          <SectionLabel as="h3">{`Not on the clock (${off.length})`}</SectionLabel>
          {off.length === 0 ? (
            <p className="or-strip__empty">Everybody is on the clock.</p>
          ) : (
            <div className="or-strip">
              {off.map((w) => (
                <div key={w.id} className="or-strip__chip" data-tone="off">
                  <Avatar initials={initialsOf(w.name)} tone="neutral" />
                  <div>
                    <div className="or-strip__name">{w.name}</div>
                    <div className="or-strip__meta">{lastWorked(w)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : null}
    </Panel>
  );
}

function WorkerDetail({ worker, canUpdate, canSensitive, canSeeJobs, settings, onClose }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(() => seed(worker, canSensitive));
  const { busy, error, run, setError } = useAction();
  // `enabled`, not a render guard. GET /workers/:id/jobs is checkRole('admin',
  // 'manager'), so for an accounting user this request can only ever be a 403
  // — and one that costs a round trip and lands in the console every time they
  // open anybody's record.
  const jobsQ = useOfficeQuery(['worker-jobs', worker.id], `/workers/${worker.id}/jobs`, { enabled: canSeeJobs });

  const week = useMemo(() => getCurrentWeekRange(settings), [settings]);
  const [period, setPeriod] = useState({ start: week.startDate, end: week.endDate });
  // Which document this button produces, and it is not a cosmetic preference.
  // GET /time-entries/paystub/:id builds from APPROVED timesheet entries when
  // approved_only === 'true' and from the raw clock otherwise — absent reads
  // as "pay un-approved time" (server/routes/timeEntries.js:766 vs the
  // trailing else). Defaults to approved, as the modal this replaced did.
  const [approvedOnly, setApprovedOnly] = useState(true);
  const pay = useAction();

  useEffect(() => { setForm(seed(worker, canSensitive)); setEditing(false); }, [worker, canSensitive]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError('A name is required.'); return; }
    const body = profileBody(form, worker, canSensitive);
    if (Object.keys(body).length === 0) { setEditing(false); return; }
    const ok = await run(async () => {
      await put(`/workers/${worker.id}`, body);
      await qc.invalidateQueries({ queryKey: ['workers'] });
    }, 'Could not save the profile.');
    if (ok) setEditing(false);
  };

  const paystub = () => pay.run(
    () => downloadBlob(
      `/time-entries/paystub/${worker.id}?start_date=${encodeURIComponent(period.start)}`
        + `&end_date=${encodeURIComponent(period.end)}`
        + (approvedOnly ? '&approved_only=true' : ''),
      `paystub-${worker.name.replace(/\s+/g, '-').toLowerCase()}-${period.start}.pdf`
    ),
    'Could not fetch the paystub.'
  );

  return (
    <Panel title={worker.name} actions={<FwButton variant="quiet" icon="x" onClick={onClose} aria-label="Close detail" />}>
      <FormError error={error} />
      {editing ? (
        <form className="or-form" onSubmit={save} noValidate>
          <FwField label="Name" required htmlFor="or-crew-name"><FwInput value={form.name} onChange={set('name')} /></FwField>
          <FormRow>
            <FwField label="Email" htmlFor="or-crew-email"><FwInput type="email" value={form.email} onChange={set('email')} /></FwField>
            <FwField label="Phone" htmlFor="or-crew-phone"><FwInput type="tel" value={form.phone_number} onChange={set('phone_number')} /></FwField>
          </FormRow>
          <FwField label="Address" htmlFor="or-crew-address"><FwInput value={form.address} onChange={set('address')} /></FwField>
          <FormRow>
            <FwField label="City" htmlFor="or-crew-city"><FwInput value={form.city} onChange={set('city')} /></FwField>
            <FwField label="State" htmlFor="or-crew-state"><FwInput value={form.state} onChange={set('state')} maxLength={2} /></FwField>
            <FwField label="ZIP" htmlFor="or-crew-zip"><FwInput value={form.zip_code} onChange={set('zip_code')} inputMode="numeric" /></FwField>
          </FormRow>
          <FormRow>
            <FwField label="Emergency contact" htmlFor="or-crew-ec"><FwInput value={form.emergency_contact} onChange={set('emergency_contact')} /></FwField>
            <FwField label="Emergency phone" htmlFor="or-crew-ep"><FwInput type="tel" value={form.emergency_phone} onChange={set('emergency_phone')} /></FwField>
          </FormRow>
          <FormRow>
            <FwField label="Hire date" htmlFor="or-crew-hire"><FwInput type="date" value={form.hire_date} onChange={set('hire_date')} /></FwField>
            {canSensitive ? (
              <FwField label="Hourly rate" htmlFor="or-crew-rate" hint="Feeds payroll. Leave as is unless it changed.">
                <FwInput type="number" min="0" step="0.01" inputMode="decimal" value={form.hourly_rate} onChange={set('hourly_rate')} />
              </FwField>
            ) : null}
          </FormRow>
          <FwField label="Notes" htmlFor="or-crew-notes"><FwTextarea value={form.notes} onChange={set('notes')} rows={3} /></FwField>
          <div className="or-sheet__footer">
            <FwButton variant="ghost" onClick={() => { setForm(seed(worker, canSensitive)); setEditing(false); }} disabled={busy}>Cancel</FwButton>
            <FwButton type="submit" loading={busy}>Save profile</FwButton>
          </div>
        </form>
      ) : (
        <>
          <DetailList>
            <DetailRow label="Role"><Pill tone={ROLE_TONE[worker.role] || 'neutral'}>{roleLabel(worker.role)}</Pill></DetailRow>
            <DetailRow label="Status"><ActivePill active={worker.is_active} /></DetailRow>
            <DetailRow label="Email">{textOr(worker.email)}</DetailRow>
            <DetailRow label="Phone">{textOr(worker.phone_number)}</DetailRow>
            <DetailRow label="Address">{[worker.address, worker.city, worker.state, worker.zip_code].filter(Boolean).join(', ') || '—'}</DetailRow>
            <DetailRow label="Emergency">{[worker.emergency_contact, worker.emergency_phone].filter(Boolean).join(' · ') || '—'}</DetailRow>
            <DetailRow label="Hired">{fmtDate(worker.hire_date)}</DetailRow>
            {canSensitive ? <DetailRow label="Rate">{fmtRate(worker.hourly_rate)}</DetailRow> : null}
            <DetailRow label="Hours">{hoursOf(worker.total_time_worked)}</DetailRow>
            {worker.notes ? <DetailRow label="Notes">{worker.notes}</DetailRow> : null}
          </DetailList>
          <div className="or-actions">
            {canUpdate ? <FwButton variant="ghost" icon="edit" onClick={() => setEditing(true)}>Edit profile</FwButton> : null}
          </div>
          <p className="or-muted">
            SSN and encryption are on <Link className="or-link" to="/security">Security</Link>.
          </p>
        </>
      )}

      <Panel title="Paystub PDF">
        <FormError error={pay.error} />
        <FormRow>
          <FwField label="From" htmlFor={`or-pay-start-${worker.id}`}>
            <FwInput type="date" value={period.start} onChange={(e) => setPeriod((p) => ({ ...p, start: e.target.value }))} />
          </FwField>
          <FwField label="To" htmlFor={`or-pay-end-${worker.id}`}>
            <FwInput type="date" value={period.end} onChange={(e) => setPeriod((p) => ({ ...p, end: e.target.value }))} />
          </FwField>
        </FormRow>
        <label className="or-form__check">
          <input type="checkbox" checked={approvedOnly} onChange={(e) => setApprovedOnly(e.target.checked)} />
          Approved hours only
        </label>
        <p className="or-note">
          {approvedOnly
            ? 'Built from approved timesheets — the hours a manager signed off.'
            : 'Built from raw clock time, including hours nobody has approved yet.'}
        </p>
        <div className="or-actions">
          <FwButton variant="ghost" icon="download" onClick={paystub} loading={pay.busy} disabled={!period.start || !period.end}>Download paystub</FwButton>
        </div>
      </Panel>

      <Panel className="or-panel--flush" title="Recent jobs">
        {/* `.or-panel__inset`, not a nested `.or-panel__body`. The rule that
            strips padding for a flush panel is a DESCENDANT selector
            (`.or-panel--flush .or-panel__body`), so a nested body inherited the
            zero it was added to undo and both of these messages rendered
            edge-to-edge against the panel border. */}
        {/* A disabled query stays `isPending` forever, so this branch has to
            come first — otherwise the panel is a skeleton that never resolves,
            which reads as a hung screen rather than as an answer. */}
        {!canSeeJobs ? (
          <div className="or-panel__inset">
            <p className="or-muted">Only an admin or a manager can see the jobs a person has led.</p>
          </div>
        ) : null}
        {canSeeJobs && jobsQ.isPending ? <div className="or-panel__inset"><Skeleton lines={3} /></div> : null}
        {jobsQ.error ? <div className="or-panel__inset"><LoadError error={jobsQ.error} fallback="Could not load this worker's jobs." /></div> : null}
        {/* Array.isArray, not truthiness. A non-array 200 — an error object, a
            proxy's HTML page — is truthy, and DataTable would then call
            .length on it and take the screen down with a render error rather
            than showing the message beside it. */}
        {Array.isArray(jobsQ.data) ? <DataTable dense columns={JOB_COLUMNS} rows={jobsQ.data} empty="No jobs led yet." caption={`Jobs led by ${worker.name}`} /> : null}
      </Panel>
    </Panel>
  );
}

export default function Crew() {
  // Two different questions, answered by the two different mechanisms the
  // SERVER uses — see WORKER_ENDPOINTS above for why that distinction is the
  // whole fix. read_sensitive is a permission the server reads through
  // userHasPermission; the edit and the jobs list are hard role lists.
  const canSensitive = useCan('workers', 'read_sensitive');
  const role = useRole();
  const canUpdate = mayUseWorkerEndpoint('updateProfile', role);
  const canSeeJobs = mayUseWorkerEndpoint('recentJobs', role);
  const { settings } = useSettings();
  // Which side of the one breakpoint this is — the same hook MoreFilters and
  // PageHeader already use, not a new number. It decides only WHERE the clock
  // panel goes, never whether it renders.
  const phone = usePhoneLayout();
  const [showInactive, setShowInactive] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [created, setCreated] = useState(null);
  const qcCrew = useQueryClient();
  const workersQ = useOfficeQuery(['workers', showInactive ? 'all' : 'active'], showInactive ? '/workers?include_inactive=true' : '/workers');

  const [q, setQ] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const workers = useMemo(() => workersQ.data || [], [workersQ.data]);
  const rows = useMemo(() => workers.filter((w) => matches(w, ['name', 'email', 'phone_number', 'role'], q)), [workers, q]);
  const selected = workers.find((w) => w.id === selectedId) || null;

  /**
   * The columns — and on a phone, what a CARD is allowed to say about a person.
   *
   * This is the difference a card makes and the reason the roles here are not
   * the obvious ones. A table row is scanned; a card is READ, from across a
   * truck cab, by whoever is standing next to whoever is holding the phone. A
   * field that was the seventh cell of a wide row is now a headline. So the
   * question stopped being "may this person see it" — the server and
   * `workers.read_sensitive` already answered that — and became "does it need
   * to be legible at arm's length to look somebody up".
   *
   * Card: name (+ the On-clock pill it already carries), role beneath it,
   * Active/Inactive as the badge, and how to REACH them as the one fact,
   * because "get hold of this person" is what this screen is opened for.
   *
   * ── That fact was a dash for everybody (measured 2026-09-11) ──────────────
   *
   * It read `phone_number` alone. In a browser at 320px, against a crew added
   * the way crew are actually added, every card's only fact was:
   *
   *     Phone  —
   *
   * because `POST /api/admin/users` does not take a phone number, so a person
   * created through Users has none until somebody opens their profile and
   * types one. A 129px card whose single fact is a dash is the owner's
   * "nothing that makes sense" stated exactly.
   *
   * So the slot is "Contact" and it falls through: the phone first — this
   * screen is opened to ring somebody standing in a yard, and that decision is
   * unchanged — then the email, then a sentence. The label had to widen with
   * the value; "Phone: pat@…" would be a lie in the tidiest possible form.
   *
   * `email` is on every row of GET /api/workers and is NOT sensitive —
   * SENSITIVE_WORKER_FIELDS is `['hourly_rate', 'ssn_hash']` and nothing else
   * (server/routes/workers.js:17) — so this shows no one anything the list did
   * not already carry. It is not a permission change and must not be read as
   * one. The note below about the email being *hidden as a column* was about
   * the fact GRID being spent on a long string that truncates; here it is the
   * fallback for a slot that was empty.
   *
   * `hidden` is table-only and card-only-hidden — the wide table is unchanged
   * and every one of these is still on the worker's own detail panel:
   *   hourly_rate  a PAYROLL FIGURE. It is already gated on
   *                workers.read_sensitive, so this is not a permission change
   *                and must not be read as one; it is that a rate sat beside a
   *                name in 19px on a phone is the most over-shared thing this
   *                screen could produce, and nobody looks a colleague up in
   *                order to read their rate. It is on the detail panel, for
   *                the same permission, one tap away.
   *   email        long, truncates to nothing useful in a fact slot, and the
   *                phone is the contact that works in a yard.
   *   hire_date    a personnel-record date. Never the reason this screen is
   *                open, and the least defensible thing to leave on a screen
   *                someone else can read over your shoulder.
   */
  const columns = useMemo(() => {
    const cols = [
      { key: 'name', label: 'Name', role: 'title', render: (w) => <span>{w.name}{w.is_clocked_in ? <> <Pill tone="now">On clock</Pill></> : null}</span> },
      { key: 'role', label: 'Role', role: 'subtitle', render: (w) => <Pill tone={ROLE_TONE[w.role] || 'neutral'}>{roleLabel(w.role)}</Pill> },
      { key: 'email', label: 'Email', role: 'hidden', render: (w) => textOr(w.email) },
      { key: 'phone_number', label: 'Contact', role: 'fact', render: (w) => factFallback([w.phone_number, w.email], 'None on file') },
      { key: 'hire_date', label: 'Hired', role: 'hidden', render: (w) => fmtDate(w.hire_date) },
    ];
    if (canSensitive) cols.push({ key: 'hourly_rate', label: 'Rate', align: 'right', role: 'hidden', render: (w) => fmtRate(w.hourly_rate) });
    cols.push({ key: 'is_active', label: 'Status', role: 'badge', render: (w) => <ActivePill active={w.is_active} /> });
    return cols;
  }, [canSensitive]);

  const table = workersQ.isPending ? <Skeleton lines={6} /> : workersQ.error ? (
    <LoadError error={workersQ.error} fallback="Could not load the crew." />
  ) : workers.length === 0 ? (
    <EmptyState icon="users" title="No crew yet">People are added from Users; they appear here once they have a role of worker, manager or accounting.</EmptyState>
  ) : (
    <Panel className="or-panel--flush">
      <DataTable
        columns={columns}
        rows={rows}
        onRowClick={(w) => setSelectedId(w.id)}
        empty={q ? `Nobody matches "${q}".` : 'No crew.'}
        caption="Crew"
      />
    </Panel>
  );

  return (
    <div className="or-page" data-screen="office-Crew">
      {/*
        `N of M`, the form Jobs and Users already use, and not "N people".
        Measured at 390px on 2026-09-10: a manager's header read "1 person"
        directly above a panel reading "Not on the clock (3)". Both were right
        and they count different populations — the header is the roster THIS
        reader may open (GET /workers narrows by paystubs.manage_all), the
        panel is company-wide (see ClockedInStrip). A bare "1 person" over a
        bare "(3)" reads as one crew counted twice; "1 of 1" is plainly about
        the list underneath, and it follows the search box, which the old
        count did not — searching left "3 people" over one matching card.
      */}
      {/* Add crew member — admins only, because POST /api/admin/users is
          checkRole('admin'). The owner reached for it here (2026-09-26); it
          was only under Admin → Users. The password may be left blank: they
          are emailed their own temporary one (admin/NewAccountSheet.js). */}
      <PageHeader title="Crew" meta={workersQ.data ? `${rows.length} of ${workers.length}` : null}>
        {role === 'admin' ? <FwButton icon="plus" onClick={() => setAddOpen(true)} data-action="add-crew">Add crew member</FwButton> : null}
      </PageHeader>
      <WelcomeNotice created={created} onDismiss={() => setCreated(null)} />
      <NewAccountSheet
        open={addOpen}
        title="Add crew member"
        onClose={() => setAddOpen(false)}
        onCreated={(made) => { setAddOpen(false); setCreated(made); qcCrew.invalidateQueries({ queryKey: ['workers'] }); }}
      />
      {/*
        ── On a phone the clock panel WAS the screen (measured 2026-09-11) ────

        At 320px x 780px, three people on the roster and nobody on the clock:

          "Clocked in now" panel   y=108, 480px tall — 62% of the viewport
            of which the off-clock chips            267px
          search toolbar           y=607
          FIRST CREW CARD          y=732 — 48px of a 129px card above the fold

        At 390px: panel 436px, first card y=688. Somebody opening the screen
        called Crew, to look a person up, got a sliver of one crew member under
        two thirds of a screen of status. The note on the scope sentence inside
        ClockedInStrip already recorded that this list "had been flagged as
        starting too low" and trimmed a wrapped line to buy 54px; the line was
        never the problem, the panel's POSITION was.

        So on a phone it moves BELOW the roster. Nothing is hidden, nothing is
        behind a press, and every chip keeps its name, its last-worked date and
        its live clock — which matters because those are a DECISION, not an
        accident: docs/PARITY_2026-09-08.md §18 is the capability that was
        missing until 2026-09-09, and scripts/ui-check.js `crew-off-clock` is
        phone-only precisely so it stays measurable at the widths crews carry.

        A first draft collapsed the chips behind a "Show names" toggle instead.
        It read well in jsdom and the browser gate refused it — 5 reds on
        crew-off-clock, because the people it asserts are on a phone screen
        were behind a press. Reordering satisfies that guard rather than
        working around it, and it is the better answer anyway: a roster you
        scroll PAST the status to reach is a worse screen than one where the
        status is the thing you scroll to.

        Desktop is untouched: there is room for both, and the status belongs at
        the top where a dispatcher looks first.
      */}
      {phone ? null : <ClockedInStrip />}
      <Toolbar>
        <SearchBox value={q} onChange={setQ} placeholder="Search crew" label="Search crew" />
        <label className="or-form__check">
          <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
          Show inactive
        </label>
      </Toolbar>
      <Split
        main={table}
        aside={selected ? (
          <WorkerDetail
            key={selected.id}
            worker={selected}
            canUpdate={canUpdate}
            canSensitive={canSensitive}
            canSeeJobs={canSeeJobs}
            settings={settings}
            onClose={() => setSelectedId(null)}
          />
        ) : null}
      />
      {/* The same panel, after the roster, on a phone only — see the note
          above the desktop position. Exactly one of the two is ever mounted,
          so this is one component and one request, not two. */}
      {phone ? <ClockedInStrip /> : null}
    </div>
  );
}
